import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { SignOutComponent } from '@components/signin/sign-out/sign-out.component';
import { SigninPasswordComponent } from '@components/signin/signin-password/signin-password.component';
import { SigninRoleComponent } from '@components/signin/signin-role/signin-role.component';
import { SwitchAccountsComponent } from '@components/signin/switch-accounts/switch-accounts.component';
import { AlarmStatusCount } from '@models/alarm';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@service/auth.service';
import { LanguageService } from '@service/language.service';
import { OpsAlarmMonitoringService } from '@service/ops-alarms/ops-alarm-monitoring.service';
import { ResponsiveService } from '@service/responsive.service';
import { AlarmMonitoring } from 'ops/common/models/alarm-monitoring';

import { Subscription } from 'rxjs';
import { EnvironmentSettingsComponent } from './environment-settings/environment-settings.component';
import { DeviceFlagsService } from '@service/device-flags.service';
import { AlarmLevel } from 'ops/common/models/alarm-level';

@Component({
    selector: 'app-ops-header',
    templateUrl: './ops-header.component.html',
    styleUrls: ['./ops-header.component.scss'],
    standalone: false,
})
export class OpsHeaderComponent implements OnInit, OnDestroy {
    localtime: string;
    utctime: string;

    alarms: AlarmMonitoring[] = [];
    alarmIndex: number = 0;
    latestAlarm: AlarmMonitoring;
    alarmStatusCount: AlarmStatusCount = {
        [AlarmLevel.Alarm]: 0,
        [AlarmLevel.Caution]: 0,
        [AlarmLevel.Warning]: 0,
    };
    isBuzzerActive: boolean;
    alarmWindowOpen: boolean;
    currnetLang: string;

    newAlarmSubscription: Subscription;
    updateAlarmSubscription: Subscription;
    alarmStatusCountSubject: Subscription;

    @ViewChild('signInRole')
    signInRole: SigninRoleComponent;
    @ViewChild('signInPassword')
    signInPassword: SigninPasswordComponent;
    @ViewChild('signOut')
    signOut: SignOutComponent;
    @ViewChild('switchAccounts')
    switchAccounts: SwitchAccountsComponent;
    isLoggedinSubscription: Subscription;

    @ViewChild('environmentSettings')
    environmentSettings: EnvironmentSettingsComponent;

    isLoggedIn: boolean;
    statusMaintenance = true;
    statusAlarm = true;

    @Input() sidebarHidden: boolean;
    @Output() onSidebarShow = new EventEmitter();
    @Input() sidebarExtends: boolean;
    @Output() onSidebarExtends = new EventEmitter();

    constructor(
        public translate: TranslateService,
        private alarmMonitoringService: OpsAlarmMonitoringService,
        private authService: AuthService,
        public responsive: ResponsiveService,
        private languageService: LanguageService,
        private deviceFlagsService: DeviceFlagsService,
    ) {}

    ngOnDestroy(): void {
        this.newAlarmSubscription.unsubscribe();
        this.updateAlarmSubscription.unsubscribe();
        this.alarmStatusCountSubject.unsubscribe();
        this.isLoggedinSubscription.unsubscribe();
    }

    ngOnInit(): void {
        this.setTime();
        setInterval(() => {
            this.setTime();
        }, 1000);

        this.updateAlarm();

        this.newAlarmSubscription = this.alarmMonitoringService
            .onNewAlarm()
            .subscribe((v: AlarmMonitoring) => {
                this.updateAlarm();
            });

        this.updateAlarmSubscription = this.alarmMonitoringService.onAlarmsUpdated$.subscribe(
            (v) => {
                this.updateAlarm();
            },
        );

        this.alarmStatusCountSubject = this.deviceFlagsService
            .onAlarmStatusCountUpdated()
            .subscribe((alarmCount) => {
                this.alarmStatusCount = alarmCount;
            });

        this.currnetLang = this.languageService.getCurrentLanguage().value;

        this.isLoggedinSubscription = this.authService.isLoggedin$.subscribe((result) => {
            this.isLoggedIn = result;
            if (!this.isLoggedIn) {
                this.closeSwitchAccounts();
            }
        });
    }

    updateAlarm() {
        this.alarms = [...this.alarmMonitoringService.getAlarms()];
        this.alarmIndex = Math.max(this.alarms.length - 1, 0);
        this.latestAlarm = this.alarms[this.alarms.length - 1];
        this.isBuzzerActive = this.alarmMonitoringService.isBuzzerActive();
    }

    setTime() {
        const d = new Date();
        const utc = d
            .toISOString()
            .substring(d.toISOString().indexOf('T') + 1, d.toISOString().indexOf('T') + 6);
        this.utctime = String(utc);
        this.localtime = String(
            d.getHours().toString().padStart(2, '0') +
                ':' +
                d.getMinutes().toString().padStart(2, '0'),
        );
    }

    public onAlarmWindowOpen() {
        this.alarmWindowOpen = !this.alarmWindowOpen;
        if (this.sidebarExtends) {
            this.onSidebarExtends.emit();
        }
    }

    public onAlarmWindowClose() {
        this.alarmWindowOpen = false;
    }

    public onStopBuzzer() {
        this.alarmMonitoringService.clickStopBuzzerData();
        this.isBuzzerActive = this.alarmMonitoringService.isBuzzerActive();
    }

    public onShowSigninRoleOverlay() {
        this.signInRole.setVisible(true);
    }

    public onShowSigninPasswordOverlay(
        selectedRole: { id: string; role: string },
        isSwitchAccounts: boolean,
    ) {
        this.signInRole.setVisible(false);
        this.signInPassword.setVisible(true, selectedRole, isSwitchAccounts);
        document.removeEventListener('click', this.closeSwitchAccounts);
    }

    public setSidePanelEvent() {
        document.addEventListener('click', this.closeSwitchAccounts);
    }

    public onOpenSwitchAccounts(event: MouseEvent) {
        event.stopImmediatePropagation();
        this.switchAccounts.setVisible();
        document.addEventListener('click', this.closeSwitchAccounts);
    }

    public closeSwitchAccounts = () => {
        if (this.switchAccounts) {
            this.switchAccounts.closePanel();
        }
        document.removeEventListener('click', this.closeSwitchAccounts);
    };

    public onOpenSidebar() {
        this.onSidebarExtends.emit();
        this.onSidebarShow.emit();
        if (this.alarmWindowOpen) {
            this.alarmWindowOpen = !this.alarmWindowOpen;
        }
    }

    public onPreviousAlarm() {
        this.alarmIndex = Math.min(this.alarmIndex + 1, Math.max(this.alarms.length - 1, 0));
    }

    public onNextAlarm() {
        this.alarmIndex = Math.max(this.alarmIndex - 1, 0);
    }

    public onOpenEnvironmentSettings() {
        this.environmentSettings.onShownEvironmentSettings();
    }

    setLanguage(code: string) {
        this.languageService.setCurrentLanguage(code);
        this.currnetLang = this.languageService.getCurrentLanguage().value;
    }
}
